import { useEffect, useRef, type ReactNode } from 'react';
import {
  CAROUSEL_INERTIA_MIN_SPEED,
  carouselFingerVelocity,
  carouselInertiaFrame,
  decideCarouselAxis,
  HUB_CAROUSEL_SCROLLER,
} from '@/components/hub/hubCarousel';

type HubCarouselScrollerProps = {
  children: ReactNode;
  testId?: string;
};

type TouchSample = { t: number; x: number };

/**
 * Horizontal peek row that does not trap vertical page scroll on phones.
 * Native `overflow-x` still handles trackpads; touch uses an axis lock, then
 * coasts so a sideways swipe is not a dead 1:1 drag.
 */
export function HubCarouselScroller({ children, testId }: HubCarouselScrollerProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let startX = 0;
    let startY = 0;
    let startScroll = 0;
    let axis: 'x' | 'y' | null = null;
    let dragged = false;
    let samples: TouchSample[] = [];
    let raf = 0;

    const maxScroll = () => Math.max(0, el.scrollWidth - el.clientWidth);

    const clampScroll = (left: number) => Math.min(maxScroll(), Math.max(0, left));

    const stopInertia = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    };

    const restoreSnap = () => {
      el.style.scrollSnapType = '';
    };

    const prefersReducedMotion = () =>
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const runInertia = (fingerVelocity: number) => {
      stopInertia();
      let velocity = fingerVelocity;
      let last = performance.now();
      const tick = (now: number) => {
        const dt = Math.min(32, now - last);
        last = now;
        const next = carouselInertiaFrame(el.scrollLeft, velocity, dt, maxScroll());
        el.scrollLeft = next.scrollLeft;
        velocity = next.velocity;
        if (next.done) {
          raf = 0;
          restoreSnap();
          return;
        }
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    };

    const onTouchStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!touch) return;
      stopInertia();
      startX = touch.clientX;
      startY = touch.clientY;
      startScroll = el.scrollLeft;
      axis = null;
      dragged = false;
      samples = [{ t: performance.now(), x: touch.clientX }];
    };

    const onTouchMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!touch) return;
      const now = performance.now();
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      if (!axis) {
        axis = decideCarouselAxis(dx, dy);
        if (!axis) return;
      }
      if (axis !== 'x') return;
      dragged = true;
      // Mandatory snap fights JS `scrollLeft` and makes the row lag the finger.
      el.style.scrollSnapType = 'none';
      el.scrollLeft = clampScroll(startScroll - dx);
      samples.push({ t: now, x: touch.clientX });
      if (samples.length > 10) samples = samples.slice(-10);
      if (event.cancelable) event.preventDefault();
    };

    const onTouchEnd = () => {
      if (axis !== 'x') {
        restoreSnap();
        return;
      }
      if (prefersReducedMotion()) {
        restoreSnap();
        return;
      }
      const velocity = carouselFingerVelocity(samples, performance.now());
      if (Math.abs(velocity) < CAROUSEL_INERTIA_MIN_SPEED) {
        restoreSnap();
        return;
      }
      runInertia(velocity);
    };

    const onClickCapture = (event: Event) => {
      if (!dragged) return;
      event.preventDefault();
      event.stopPropagation();
      dragged = false;
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchEnd, { passive: true });
    el.addEventListener('click', onClickCapture, true);
    return () => {
      stopInertia();
      restoreSnap();
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
      el.removeEventListener('click', onClickCapture, true);
    };
  }, []);

  return (
    <div ref={ref} data-testid={testId} className={HUB_CAROUSEL_SCROLLER}>
      {children}
    </div>
  );
}
